import { SearchIcon, FilterIcon } from "@heroicons/react/outline";
import moment from "moment-timezone";
import { ChangeEvent,  useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import SessionModel from "../../api/models/SessionModel";
import { useScrollEndListener } from "../../hooks/useScrollEndListener";
import { useScrollIntoView } from "../../hooks/useScrollIntoView";
import SessionCard from "./SessionCard";
import ShowIf from "../show-if/ShowIf";
import AppFilters from "../app-filters/AppFilters";
import { useInfiniteTutorStudents } from "../../hooks/useInfiniteTutorStudents";
import PlaceHolder from "../placeHolder.tsx/PlaceHolder";

type SessionsListStickyHeadingsProps = {
  sessions: any;
  onClick?: (sessionId: number) => void;
  userType: "student" | "tutor";
  currentInViewCardId?: number;
  onReachTop?: () => void;
  onSearch: (val: object | null) => void;
  filters: { [key: string]: any };
  enableStudents: boolean;
};
export default function SessionsListStickyHeadings({
  sessions,
  onClick,
  userType,
  currentInViewCardId,
  onReachTop,
  onSearch,
  filters,
  enableStudents,
}: SessionsListStickyHeadingsProps) {
  const [searchTerm, setSearchTerm] = useState<string | null>(null);
  const [debouncedSearchTerm, setDebouncedSearchTerm] =
    useState<string | null>(null);

  const LocalBasePath = `page.${userType}.dashboard.sessionsList`;
  const SessionLocal = useTranslation("", { keyPrefix: LocalBasePath });
  const currentSessionRef = useScrollIntoView<HTMLButtonElement>(0, {
    block: "center",
  });
  const LocalBasePathSub = "page.tutor.dashboard";
  const { t } = useTranslation("", { keyPrefix: LocalBasePathSub });

  const [containerRef, reachedTop] = useScrollEndListener<HTMLElement>({
    offsetTop: 500, // The height of each session card is about 220px, So we load more sessions when the user is two sessions away from the top
  });
  const [showFilters, setShowFilters] = useState(false);
  const onClickSession = (session: SessionModel) => {
    if (session.isDone || session.isInProgress) {
      if (!!onClick) onClick(session.id);
    }
  };

  const onCancelFilters = () => {
    setShowFilters(false);
  };
  const { students, loadMoreStudents } =
    useInfiniteTutorStudents(enableStudents);
  const studentsOptions = students.map((student) => ({
    key: student.name,
    value: `${student.id}`,
  }));
  useEffect(() => {
    if (reachedTop && !!onReachTop) onReachTop();
  }, [reachedTop]);

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => {
      clearTimeout(debounceTimer);
    };
  }, [searchTerm]);

  useEffect(() => {
    if (!!debouncedSearchTerm) {
      onSearch({ ...filters, q: debouncedSearchTerm });
    } else {
      onSearch(null);
    }
  }, [debouncedSearchTerm]);

  const handleSearchInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value != "" ? e.target.value : null);
  };
  return (
    <nav
      className="h-full overflow-y-scroll"
      aria-label="Directory"
      ref={containerRef}
      data-cy="sessionsListContainer"
    >
      <div className="z-[11] sticky top-0 border-t border-b border-gray-200 bg-gray-50 px-6 py-1 flex justify-between	 text-sm font-medium text-gray-500">
        <div className="w-full p-7">
          <div>
            <label htmlFor="email" className="block pb-2 text-lg font-medium ">
              {SessionLocal.t("readingSessions")}
            </label>
            <div className="mt-1 relative rounded-md shadow-sm flex justify-between gap-[18px]">
              <div className="absolute inset-y-0 flex items-center pointer-events-none rtl:right-0 lrt:left-0 rtl:pr-3 ltr:pl-3">
                <SearchIcon
                  className="w-5 h-5 text-gray-400"
                  aria-hidden="true"
                />
              </div>
              <input
                type="text"
                name="search"
                id="search"
                className="block w-full border-gray-300 rounded-md focus:ring-brandblue-500 focus:border-brandblue-500 rtl:pr-10 ltr:pl-10 sm:text-sm"
                placeholder={SessionLocal.t("search")}
                onChange={handleSearchInputChange}
              />
              {userType === "tutor" && (
                <button
                  type="button"
                  className="inline-flex items-center p-2 border border-gray-300 rounded-md shadow-sm text-gray-500 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brandblue-500"
                  onClick={() => setShowFilters((x) => !x)}
                >
                  <FilterIcon className="h-5 w-5" aria-hidden="true" />
                </button>
              )}
            </div>
          </div>
          <ShowIf condition={showFilters}>
            <AppFilters
              className="pb-1 pt-5 flex gap-4"
              filters={filters}
              setFilters={(val: any) => {
                const studentVal = val();
                if (!!studentVal && studentVal.student_id != "all") {
                  onSearch({ ...filters, ...studentVal });
                } else {
                  onSearch(null);
                }
              }}
              onLoadMore={(key: string) => loadMoreStudents()}
              fields={[
                {
                  name: "student_id",
                  label: t("schedule.reader"),
                  options: studentsOptions,
                  paginationLabel: "currentStudentsPage",
                },
              ]}
              handleButtonClick={onCancelFilters}
            />
          </ShowIf>
        </div>
      </div>

      <>
        {Object.keys(sessions).length > 0 ? (
          <>
            {Object.keys(sessions).map((ids: any, index: any) => (
              <div key={`heading_${index}_${ids}`} className="relative">
                <div className="z-10 sticky top-[144px] border-t border-b border-gray-200 bg-gray-50 px-6 py-1 flex justify-between	 text-sm font-medium text-gray-500">
                  <h3>{moment(ids, "MM-YYYY").format("MMM")}</h3>
                  <h3>{moment(ids, "MM-YYYY").format("YYYY")}</h3>
                </div>
                <ul
                  role="group"
                  className="relative z-0 divide-y divide-gray-200"
                  data-cy="sessionsList"
                >
                  {sessions[ids].map((session: SessionModel, index: any) => {
                    return (
                      <button
                        key={`sessions_cards_${session.id}_${index}`}
                        className="w-full"
                        onClick={() => onClickSession(session)}
                        ref={
                          session.id == currentInViewCardId
                            ? currentSessionRef
                            : null
                        }
                      >
                        <SessionCard
                          session={session}
                          userType={userType}
                          isSessionIdShown={
                            session.isDone || session.isInProgress
                          }
                        />
                      </button>
                    );
                  })}
                </ul>
              </div>
            ))}
          </>
        ) : (
          <div className="flex items-center justify-center w-full h-screen ">
            <PlaceHolder title="noSestionsTitle" />
          </div>
        )}
      </>
    </nav>
  );
}
